import {ApplicationConfig, ErrorHandler, inject, Injectable} from '@angular/core';
import {provideRouter, TitleStrategy, withComponentInputBinding} from '@angular/router';
import {HttpErrorResponse, provideHttpClient, withInterceptors} from '@angular/common/http';
import {provideAnimationsAsync} from '@angular/platform-browser/animations/async';
import {providePrimeNG} from 'primeng/config';
import {ConfirmationService, MessageService} from 'primeng/api';
import {routes} from './app.routes';
import {authInterceptor} from './auth.interceptor';
import {errorInterceptor} from './error.interceptor';
import {AdminV2Theme} from './theme';
import {AdminV2TitleStrategy} from './title-strategy';
import {environment} from './environments/environment';
import {BASE_PATH} from './api-client/variables';

@Injectable({providedIn: 'root'})
export class GlobalErrorHandler implements ErrorHandler {
  private readonly messageService = inject(MessageService);

  handleError(error: unknown): void {
    console.error('An error occurred:', error);
    // errorInterceptor already toasts these with the status and the server's
    // message. Toasting again here would stack a second, contentless
    // "An unexpected error occurred." on top of it - HttpErrorResponse
    // implements Error rather than extending it, so it misses the branch below.
    if (error instanceof HttpErrorResponse) {
      return;
    }
    if (error instanceof Error) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: error.message,
        life: 5000,
      });
    } else {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'An unexpected error occurred.',
        life: 5000,
      });
    }
  }
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideHttpClient(withInterceptors([authInterceptor, errorInterceptor])),
    provideRouter(routes, withComponentInputBinding()),
    provideAnimationsAsync(),
    MessageService,
    ConfirmationService,
    {provide: ErrorHandler, useClass: GlobalErrorHandler},
    {provide: TitleStrategy, useClass: AdminV2TitleStrategy},
    {provide: BASE_PATH, useValue: environment.apiUrl},
    providePrimeNG({
      theme: {
        preset: AdminV2Theme,
        options: {
          darkModeSelector: false,
          darkMode: false,
          // Without this PrimeNG injects unlayered CSS, which outranks every
          // layered Tailwind utility regardless of specificity. `order` is the
          // layer-order statement PrimeNG emits at runtime as head.firstChild;
          // it must stay identical to the one at the top of styles.css.
          cssLayer: {
            name: 'primeng',
            order: 'properties, theme, base, primeng, components, utilities',
          },
        },
      },
    }),
  ],
};
